import { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Wrench, Camera, AlertTriangle, AlertCircle, Repeat, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { RowDeleteButton } from '@/components/common/RowDeleteButton';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

interface Event {
  id: string; code: string; vehicle_code: string | null; vehicle_plate: string | null;
  vehicle_brand: string | null; vehicle_model: string | null;
  event_type: string; priority: string; status: string;
  reported_at: string; reporter_name: string | null; reporter_driver_name: string | null;
  driver_complaint: string | null; description: string | null;
  diagnosis: string | null; diagnosed_at: string | null;
  work_started_at: string | null; work_completed_at: string | null;
  performed_by_name: string | null; verifier_name: string | null; approver_name: string | null;
  vendor_id: string | null; vendor_name: string | null;
  odometer_at_service: number | null; service_location: string | null;
  parts_cost_sar: string; labor_cost_sar: string; other_cost_sar: string; total_cost_sar: string;
  vat_sar: string;
  is_repeat_issue: boolean; prior_event_id: string | null; days_since_prior: number | null;
  next_service_due_date: string | null; next_service_due_km: number | null;
  internal_notes: string | null;
  override_no_invoice: boolean | null; override_reason: string | null;
}
interface Photo { id: string; photo_kind: string; caption: string | null; storage_url: string; taken_at: string }
interface PartUsed { id: string; part_id: string | null; part_name_text: string | null; part_number_text: string | null; quantity: string; unit_cost_sar: string; total_cost_sar: string; source: string; catalog_name_en: string | null; catalog_part_number: string | null }
interface Invoice  { id: string; code: string; invoice_number: string | null; invoice_date: string | null; total_sar: string | null; ocr_status: string | null; payment_status: string | null }
interface Audit    { id: string; action: string; prev_status: string | null; new_status: string | null; performed_at: string; performed_by_name: string | null; details: Record<string, unknown> | null }

const STATUS_TONE: Record<string, string> = {
  reported: 'bg-muted text-muted-foreground',
  diagnosed: 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
  in_progress: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  work_complete: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  pending_verification: 'bg-violet-500/15 text-violet-700 dark:text-violet-400',
  verified: 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
  approved_for_payment: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  paid: 'bg-emerald-700/15 text-emerald-800 dark:text-emerald-300',
  rejected: 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
  cancelled: 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
};

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function MaintenanceEventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [partsUsed, setPartsUsed] = useState<PartUsed[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [audit, setAudit] = useState<Audit[]>([]);
  const [tab, setTab] = useState<'overview' | 'photos' | 'parts' | 'invoice' | 'audit'>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      const r = await apiClient.get<{ event: Event; photos: Photo[]; partsUsed: PartUsed[]; invoices: Invoice[]; audit: Audit[] }>('/api/v1/maintenance/events/' + id);
      setEvent(r.data.event);
      setPhotos(r.data.photos);
      setPartsUsed(r.data.partsUsed);
      setInvoices(r.data.invoices);
      setAudit(r.data.audit);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  // PR-R3 — one-operator: no approval/verify chain. A logged event is settled
  // (paid/done) in one click.
  async function settle(paid: boolean) {
    setBusy(true);
    try { await apiClient.post(`/api/v1/maintenance/events/${id}/settle`, { paid }); await refresh(); }
    catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setBusy(false); }
  }

  async function uploadPhoto(file: File, kind: string) {
    if (!id) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    const jwt = getStoredToken();
    const res = await fetch(`${API_BASE}/api/v1/maintenance/events/${id}/photos`, {
      method: 'POST', headers: jwt ? { Authorization: 'Bearer ' + jwt } : {}, body: fd,
    });
    if (!res.ok) { window.alert('Upload failed: ' + res.statusText); return; }
    await refresh();
  }
  async function uploadInvoice(file: File) {
    if (!id) return;
    const fd = new FormData();
    fd.append('file', file);
    const jwt = getStoredToken();
    const res = await fetch(`${API_BASE}/api/v1/maintenance/events/${id}/invoices`, {
      method: 'POST', headers: jwt ? { Authorization: 'Bearer ' + jwt } : {}, body: fd,
    });
    if (!res.ok) { window.alert('Upload failed: ' + res.statusText); return; }
    await refresh();
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (error || !event) return <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6"><AlertTriangle className="h-8 w-8 text-amber-500" /><div className="text-sm">{error ?? 'Not found'}</div></div>;

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard/maintenance"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Maintenance</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Wrench className="h-4 w-4" /> {event.code}</div>
          <span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ' + (STATUS_TONE[event.status] ?? '')}>{event.status.replace(/_/g, ' ')}</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-4 space-y-4">
        {event.is_repeat_issue && (
          <div className="rounded-lg border-2 border-amber-500/30 bg-amber-500/5 p-3 text-sm inline-flex items-center gap-2">
            <Repeat className="h-4 w-4 text-amber-600" />
            <span>This is a repeat issue. Same problem on this vehicle <strong>{event.days_since_prior ?? '?'}</strong> days ago.</span>
            {event.prior_event_id && <Link to={`/dashboard/maintenance/${event.prior_event_id}`} className="underline ml-2">View prior event</Link>}
          </div>
        )}

        {/* PR-R3 — one-operator: no approval/verify chain. Logged with its
            cost and done; one click to mark paid (or reopen). */}
        <div className="rounded-lg border border-border p-3 flex flex-wrap items-center gap-3">
          {event.status === 'cancelled' ? (
            <span className="text-sm text-muted-foreground">Cancelled.</span>
          ) : event.status === 'paid' ? (
            <>
              <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-4 w-4" /> Paid / done</span>
              <Button variant="ghost" size="sm" onClick={() => settle(false)} disabled={busy}>Reopen</Button>
            </>
          ) : (
            <>
              <Button onClick={() => settle(true)} disabled={busy} className="gap-2"><CheckCircle2 className="h-4 w-4" /> Mark paid / done</Button>
              <span className="text-xs text-muted-foreground">Logged with its cost. No approval steps.</span>
            </>
          )}
          {/* PR-R6 — super_admin / admin can remove a mistakenly-added
              entry. The drawer asks for a reason; the server writes a
              permanent audit_log row (what / who / why / when) in the
              same transaction before anything is deleted, so nothing
              ever disappears without a record. */}
          <div className="ms-auto">
            <RowDeleteButton
              endpoint={`/api/v1/maintenance/events/${event.id}`}
              entityKind="maintenance entry"
              label={event.code}
              iconOnly={false}
              forceWarning="This permanently removes the entry and its photos, parts and invoices. The deletion is logged with your reason and cannot be hidden."
              onDeleted={() => navigate('/dashboard/maintenance')}
            />
          </div>
        </div>

        <div className="flex gap-2 border-b border-border overflow-x-auto">
          {([
            { v: 'overview', l: 'Overview' },
            { v: 'photos',   l: `Photos (${photos.length})` },
            { v: 'parts',    l: `Parts (${partsUsed.length})` },
            { v: 'invoice',  l: `Invoice (${invoices.length})` },
            { v: 'audit',    l: `Audit (${audit.length})` },
          ] as const).map(x => (
            <button key={x.v} onClick={() => setTab(x.v)}
              className={'px-3 py-2 text-sm border-b-2 -mb-px ' + (tab === x.v ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
              {x.l}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card label="Vehicle"           value={`${event.vehicle_code} · ${event.vehicle_plate} · ${event.vehicle_brand ?? ''} ${event.vehicle_model ?? ''}`} />
            <Card label="Event type"        value={event.event_type.replace(/_/g, ' ')} />
            <Card label="Priority"          value={event.priority.replace(/_/g, ' ')} />
            <Card label="Reported by"       value={event.reporter_name ?? event.reporter_driver_name ?? '—'} />
            <Card label="Driver complaint"  value={event.driver_complaint ?? '—'} />
            <Card label="Diagnosis"         value={event.diagnosis ?? '—'} />
            <Card label="Performed by"      value={event.performed_by_name ?? '—'} />
            <Card label="Vendor"            value={event.vendor_name ?? '—'} />
            <Card label="Odometer"          value={event.odometer_at_service ? `${event.odometer_at_service.toLocaleString()} km` : '—'} />
            <Card label="Service location"  value={event.service_location ?? '—'} />
            <Card label="Parts cost"        value={fmtSar(Number(event.parts_cost_sar))} />
            <Card label="Labor cost"        value={fmtSar(Number(event.labor_cost_sar))} />
            <Card label="Other cost"        value={fmtSar(Number(event.other_cost_sar))} />
            <Card label="Total cost"        value={fmtSar(Number(event.total_cost_sar))} />
            <Card label="Verified by"       value={event.verifier_name ?? '—'} />
            <Card label="Approved by"       value={event.approver_name ?? '—'} />
            <Card label="Next service due"  value={`${event.next_service_due_date ?? '—'}${event.next_service_due_km ? ' or ' + event.next_service_due_km + ' km' : ''}`} />
          </div>
        )}

        {tab === 'photos' && (
          <div className="space-y-3">
            <PhotoUploadStrip onUpload={uploadPhoto} />
            <PhotoGallery photos={photos} eventId={event.id} />
          </div>
        )}

        {tab === 'parts' && (
          <div className="space-y-3">
            <PartsTable partsUsed={partsUsed} eventId={event.id} onRefresh={refresh} />
          </div>
        )}

        {tab === 'invoice' && (
          <div className="space-y-3">
            <div className="rounded-lg border border-dashed border-border p-3">
              <Label>Upload invoice photo</Label>
              <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadInvoice(f); }} />
              <p className="text-[11px] text-muted-foreground mt-1">After upload, the existing OCR worker pipeline marks <code>ocr_status='extracted'</code> when the vendor block + total are confidently parsed.</p>
            </div>
            {invoices.length === 0 ? <div className="text-sm text-muted-foreground">No invoice yet.</div>
              : <table className="min-w-full text-sm rounded-lg border border-border overflow-hidden">
                  <thead className="bg-muted/30 text-xs text-muted-foreground"><tr>
                    <th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Number</th>
                    <th className="text-left px-3 py-2">Date</th><th className="text-right px-3 py-2">Total</th>
                    <th className="text-left px-3 py-2">OCR</th><th className="text-left px-3 py-2">Payment</th>
                  </tr></thead>
                  <tbody>
                    {invoices.map((i) => (
                      <tr key={i.id} className="border-t border-border">
                        <td className="px-3 py-2 font-mono text-xs">{i.code}</td>
                        <td className="px-3 py-2 text-xs">{i.invoice_number ?? '—'}</td>
                        <td className="px-3 py-2 text-xs">{i.invoice_date ? new Date(i.invoice_date).toLocaleDateString('en-GB') : '—'}</td>
                        <td className="px-3 py-2 text-right font-mono">{i.total_sar ? fmtSar(Number(i.total_sar)) : '—'}</td>
                        <td className="px-3 py-2 text-xs">{i.ocr_status ?? '—'}</td>
                        <td className="px-3 py-2 text-xs">{i.payment_status ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>}
          </div>
        )}

        {tab === 'audit' && (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="min-w-full text-xs">
              <thead className="bg-muted/30 text-muted-foreground">
                <tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">By</th><th className="text-left px-3 py-2">Action</th><th className="text-left px-3 py-2">Status change</th><th className="text-left px-3 py-2">Details</th></tr>
              </thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a.id} className="border-t border-border">
                    <td className="px-3 py-2 font-mono">{new Date(a.performed_at).toLocaleString('en-GB')}</td>
                    <td className="px-3 py-2">{a.performed_by_name ?? '—'}</td>
                    <td className="px-3 py-2">{a.action}</td>
                    <td className="px-3 py-2">{a.prev_status ?? '—'} → {a.new_status ?? '—'}</td>
                    <td className="px-3 py-2 max-w-[280px] truncate">{a.details ? JSON.stringify(a.details) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function Card({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm mt-1 break-words">{value}</div>
    </div>
  );
}

function PhotoUploadStrip({ onUpload }: { onUpload: (f: File, kind: string) => void }) {
  const [kind, setKind] = useState('before');
  return (
    <div className="rounded-lg border border-dashed border-border p-3 flex items-end gap-2 flex-wrap">
      <div className="space-y-1">
        <Label>Photo kind</Label>
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="flex h-11 rounded-md border border-input bg-background px-3 text-base">
          {['before','during','after','invoice','damage','old_part','new_part','odometer'].map(k => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      <Input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f, kind); }} className="flex-1 min-w-[14rem]" />
      <Camera className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}

function PhotoGallery({ photos, eventId }: { photos: Photo[]; eventId: string }) {
  const grouped: Record<string, Photo[]> = {};
  for (const p of photos) (grouped[p.photo_kind] ??= []).push(p);
  if (photos.length === 0) return <div className="text-sm text-muted-foreground">No photos yet.</div>;
  return (
    <div className="space-y-3">
      {Object.entries(grouped).map(([kind, items]) => (
        <div key={kind}>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{kind} ({items.length})</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {items.map((p) => (
              <div key={p.id} className="rounded-lg border border-border p-2 text-xs">
                <div className="font-mono">{kind}</div>
                {p.caption && <div className="text-muted-foreground mt-1">{p.caption}</div>}
                <div className="text-muted-foreground mt-1">{new Date(p.taken_at).toLocaleString('en-GB')}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="text-[11px] text-muted-foreground">Photo files are stored at <code>{photos[0]?.storage_url ? photos[0].storage_url.split('/').slice(0, -1).join('/') : ''}</code>. Authenticated GET endpoint TBD; signed-URL bridge for now.</div>
      <div className="text-[11px] text-muted-foreground">Event: {eventId}</div>
    </div>
  );
}

function PartsTable({ partsUsed, eventId, onRefresh }: { partsUsed: PartUsed[]; eventId: string; onRefresh: () => void }) {
  const [partId, setPartId] = useState('');
  const [partNameText, setPartNameText] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unitCostSar, setUnitCostSar] = useState('');
  const [source, setSource] = useState<'inventory' | 'fresh_purchase' | 'warranty_replacement'>('inventory');
  const [busy, setBusy] = useState(false);

  async function add() {
    if (!partId && !partNameText) { window.alert('Pick a part or enter a free-text name'); return; }
    if (!unitCostSar) { window.alert('Unit cost required'); return; }
    setBusy(true);
    try {
      await apiClient.post(`/api/v1/maintenance/events/${eventId}/parts-used`, {
        partId: partId || undefined,
        partNameText: partNameText || undefined,
        quantity: Number(quantity),
        unitCostSar: Number(unitCostSar),
        source,
      });
      setPartId(''); setPartNameText(''); setQuantity('1'); setUnitCostSar('');
      onRefresh();
    } catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setBusy(false); }
  }
  async function remove(id: string) {
    if (!window.confirm('Remove this part?')) return;
    try { await apiClient.delete(`/api/v1/maintenance/parts-used/${id}`); onRefresh(); }
    catch (e) { window.alert(extractErrorMessage(e)); }
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-dashed border-border p-3 grid grid-cols-1 md:grid-cols-6 gap-2">
        <Input placeholder="Part name (or pick from catalog)" value={partNameText} onChange={(e) => setPartNameText(e.target.value)} className="md:col-span-2" />
        <Input placeholder="Catalog UUID (opt)" value={partId} onChange={(e) => setPartId(e.target.value)} />
        <Input type="number" min={0} step="0.5" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="Qty" />
        <Input type="number" min={0} step="0.01" value={unitCostSar} onChange={(e) => setUnitCostSar(e.target.value)} placeholder="Unit SAR" />
        <select value={source} onChange={(e) => setSource(e.target.value as typeof source)} className="h-11 rounded-md border border-input bg-background px-3 text-base">
          <option value="inventory">Inventory</option>
          <option value="fresh_purchase">Fresh purchase</option>
          <option value="warranty_replacement">Warranty</option>
        </select>
        <Button onClick={add} disabled={busy} className="md:col-span-6">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Add part</Button>
      </div>

      {partsUsed.length === 0 ? <div className="text-sm text-muted-foreground">No parts logged yet.</div>
        : <table className="min-w-full text-sm rounded-lg border border-border overflow-hidden">
            <thead className="bg-muted/30 text-xs text-muted-foreground"><tr>
              <th className="text-left px-3 py-2">Part</th><th className="text-left px-3 py-2">Number</th>
              <th className="text-right px-3 py-2">Qty</th><th className="text-right px-3 py-2">Unit</th><th className="text-right px-3 py-2">Total</th>
              <th className="text-left px-3 py-2">Source</th><th className="text-right px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {partsUsed.map((p) => (
                <tr key={p.id} className="border-t border-border">
                  <td className="px-3 py-2">{p.catalog_name_en ?? p.part_name_text ?? '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs">{p.catalog_part_number ?? p.part_number_text ?? '—'}</td>
                  <td className="px-3 py-2 text-right">{Number(p.quantity)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtSar(Number(p.unit_cost_sar))}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtSar(Number(p.total_cost_sar))}</td>
                  <td className="px-3 py-2 text-xs">{p.source.replace(/_/g, ' ')}</td>
                  <td className="px-3 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => remove(p.id)}><AlertCircle className="h-3 w-3 text-rose-500" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>}
    </div>
  );
}
